import { CloudCustomerAddress } from "@omni/types/api/customer/cloud/address.model";
import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { Country } from "@omni/types/enums/country.enum";
import { Button } from "@omni/ui/components/Button/button.component";
import { useState } from "react";

import { EditAddressModal } from "@/components/Address/modals/EditAddressModal/editAddressModal.component";
import { Col } from "@/components/General/Grid/col.component";
import { Container } from "@/components/General/Grid/container.component";
import { Row } from "@/components/General/Grid/row.component";
import { DeleteAddressModal } from "@/components/Modals/DeleteAddressModal/deleteAddressModal.container";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import "./addressBlock.scss";
import { cn } from "@omni/utils/styles";

import { CustomerAddress } from "@/components/Address/Address/address.component";

type AddressBlockProps = {
    address: CloudCustomerAddress;
    updateDefaultBilling: (address: CloudCustomerAddress) => Promise<void>;
    updateDefaultDelivery: (address: CloudCustomerAddress) => Promise<void>;
    canDelete: boolean;
    addressType?: AddressType;
    updateAddress: (address: CloudCustomerAddress) => Promise<void>;
};

const AddressBlock = ({
    address,
    updateDefaultBilling,
    updateDefaultDelivery,
    canDelete,
    addressType,
    updateAddress
}: AddressBlockProps) => {
    const { t } = useGranite();
    const [isEditAddressOpen, setIsEditAddressOpen] = useState(false);
    const [isOpenDeleteAddressModal, setIsOpenDeleteAddressModal] =
        useState(false);

    return (
        <>
            <EditAddressModal
                open={isEditAddressOpen}
                setOpen={setIsEditAddressOpen}
                currentAddress={address}
                updateAddress={updateAddress}
            />
            <DeleteAddressModal
                addressId={address.id}
                isOpen={isOpenDeleteAddressModal}
                onClose={() => setIsOpenDeleteAddressModal(false)}
            />
            <Container className="profileAddressBook__address p-2">
                <Row className="mb-2">
                    <Col>
                        <CustomerAddress
                            address={address}
                            addressType={addressType}
                        />
                    </Col>
                    <Col
                        xs="auto"
                        className={cn("text-right tw:grow-0!")}
                    >
                        {canDelete && (
                            <>
                                <Button
                                    test="addressBlock-button-delete"
                                    color="link"
                                    type="button"
                                    className={"p-0"}
                                    onClick={() => {
                                        setIsOpenDeleteAddressModal(true);
                                    }}
                                >
                                    <i className="las la-trash mr-2" />
                                    <span className="d-none d-sm-inline-block">
                                        {t("address.action.delete")}
                                    </span>
                                </Button>
                                <br />
                            </>
                        )}
                        <Button
                            test="addressBlock-button-edit"
                            color="link"
                            type="button"
                            className={"p-0"}
                            onClick={() => {
                                setIsEditAddressOpen(true);
                            }}
                        >
                            <i className="las la-pencil-alt mr-2" />
                            <span className="d-none d-sm-inline-block">
                                {t("address.action.edit")}
                            </span>
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {!address.defaultDeliveryAddress && (
                            <>
                                <Button
                                    test="addressBlock-button-updateDefaultDelivery"
                                    color="link"
                                    type="button"
                                    className={"p-0"}
                                    onClick={() => {
                                        updateDefaultDelivery(address);
                                    }}
                                    disabled={address.country !== Country.Be}
                                >
                                    <i className="las la-home" />
                                    <span className="pl-2">
                                        {t(
                                            address.country === Country.Be
                                                ? "address.action.makeDefaultDelivery"
                                                : "address.action.onlyBEDelivery"
                                        )}
                                    </span>
                                </Button>
                                <br />
                            </>
                        )}
                        {!address.defaultPaymentAddress && (
                            <Button
                                test="addressBlock-button-makeDefaultPayment"
                                color="link"
                                type="button"
                                className={"p-0"}
                                onClick={() => {
                                    updateDefaultBilling(address);
                                }}
                            >
                                <i className="las la-file-invoice" />
                                <span className="pl-2">
                                    {t("address.action.makeDefaultPayment")}
                                </span>
                            </Button>
                        )}
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export { AddressBlock };
